import { chosenGoalOption, countryNamesInReadingOrder, studyTypeName } from '@sigora/domain';
import { useCallback, useEffect, useState } from 'react';

import { type Study, fetchAudience, fetchGoal, fetchGuide } from '../api/client.ts';
import { guideMinutesLabel } from '../studies/guide-minutes.ts';
import {
  SUMMARY_LABELS,
  SUMMARY_ROW_FAILED,
  SUMMARY_ROW_PENDING,
  audienceSummary,
} from './launch-copy.ts';

/** One row's read: still out, failed, or what the row says. */
export type SummaryRead<T> =
  { status: 'pending' } | { status: 'failed' } | { status: 'read'; value: T };

export interface SummaryReads {
  goal: SummaryRead<string>;
  guide: SummaryRead<string>;
  /** The count is kept beside the sentence, so the price can be held to the same N. */
  audience: SummaryRead<{ completes: number; label: string }>;
}

const PENDING: SummaryReads = {
  goal: { status: 'pending' },
  guide: { status: 'pending' },
  audience: { status: 'pending' },
};

const FAILED = { status: 'failed' } as const;

/**
 * The reads behind the four rows, made from the routes each step already reads, so the summary
 * says what those steps say and no server route composes it.
 *
 * Kept with the study they were read for, so a panel moved to another study never shows the last
 * one's rows while its own reads are out; a re-read of the same study keeps each row until its read
 * answers. `reread` asks all three again, which is what the price's "Try again" and the check of N
 * against the quote do. `refreshing` is whether any read asked for the latest token is still out,
 * so the price is not offered beside rows that may be about to change.
 */
export const useLaunchSummaryReads = ({
  studyId,
  active,
  refreshToken,
}: {
  studyId: string;
  /** Only a study that can still be paid for is summarised. */
  active: boolean;
  refreshToken: number;
}): { reads: SummaryReads; reread: () => void; refreshing: boolean } => {
  const [held, setHeld] = useState<{ studyId: string; reads: SummaryReads } | null>(null);
  const [rereadToken, setRereadToken] = useState(0);
  /** How many of the latest token's three reads are still out. */
  const [outstanding, setOutstanding] = useState(0);

  useEffect(() => {
    if (!active) {
      setHeld(null);
      setOutstanding(0);

      return;
    }

    // Torn down with the render that started it, so a read overtaken by a newer one sets nothing.
    let current = true;
    setOutstanding(3);
    const settle = <K extends keyof SummaryReads>(key: K, read: SummaryReads[K]) => {
      if (current) {
        setOutstanding((left) => left - 1);
        setHeld((last) => ({
          studyId,
          reads: { ...(last?.studyId === studyId ? last.reads : PENDING), [key]: read },
        }));
      }
    };

    fetchGoal(studyId).then(
      (view) =>
        settle(
          'goal',
          view.goal === null
            ? FAILED
            : { status: 'read', value: chosenGoalOption(view.goal.content).title },
        ),
      () => settle('goal', FAILED),
    );
    fetchGuide(studyId).then(
      (view) =>
        settle(
          'guide',
          view.dg?.status === 'approved'
            ? { status: 'read', value: guideMinutesLabel(view.dg.content) }
            : FAILED,
        ),
      () => settle('guide', FAILED),
    );
    fetchAudience(studyId).then(
      (view) => {
        if (view.audience === null) {
          settle('audience', FAILED);

          return;
        }

        const { target_completes: completes, countries } = view.audience.content;
        settle('audience', {
          status: 'read',
          value: {
            completes,
            label: audienceSummary(completes, countryNamesInReadingOrder(countries)),
          },
        });
      },
      () => settle('audience', FAILED),
    );

    return () => {
      current = false;
    };
  }, [studyId, active, refreshToken, rereadToken]);

  const reread = useCallback(() => setRereadToken((token) => token + 1), []);

  return {
    reads: held?.studyId === studyId ? held.reads : PENDING,
    reread,
    refreshing: active && outstanding > 0,
  };
};

const rowText = (read: SummaryRead<string>): string =>
  read.status === 'read'
    ? read.value
    : read.status === 'pending'
      ? SUMMARY_ROW_PENDING
      : SUMMARY_ROW_FAILED;

/**
 * What is being bought, in four rows: the Study Type, the Goal, the guide's minutes per person and
 * the audience. A description list, because each row is a label and its value.
 */
export const LaunchSummary = ({ study, reads }: { study: Study; reads: SummaryReads }) => {
  const typeName = study.studyType === null ? undefined : studyTypeName(study.studyType.id);
  const audience: SummaryRead<string> =
    reads.audience.status === 'read'
      ? { status: 'read', value: reads.audience.value.label }
      : reads.audience;

  const rows = [
    {
      key: 'type',
      label: SUMMARY_LABELS.type,
      text: rowText(typeName === undefined ? FAILED : { status: 'read', value: typeName }),
    },
    { key: 'goal', label: SUMMARY_LABELS.goal, text: rowText(reads.goal) },
    { key: 'guide', label: SUMMARY_LABELS.guide, text: rowText(reads.guide) },
    { key: 'audience', label: SUMMARY_LABELS.audience, text: rowText(audience) },
  ];

  return (
    <dl className="launch-summary summary-rows" data-test-id="launch-summary">
      {rows.map((row) => (
        <div className="launch-summary-row" key={row.key}>
          <dt>{row.label}</dt>
          <dd data-test-id={`launch-summary-${row.key}`}>{row.text}</dd>
        </div>
      ))}
    </dl>
  );
};
